import React, { memo, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client';
import {
  ProductQuery,
  ProductQueryParams,
  ProductQueryResponse,
} from '@queries/products/queries';
import { useRouter } from 'next/router';
import { SearchContentContainer } from '@components/SearchContentContainer';
import { useMekiState } from '@hooks/useMekiState';
import { Products } from '@components/Product';
import { SearchNotFound } from './searchNotFound';
import { useBottomReach } from '@hooks/useBottomReach';
import { SearchError } from './searchError';
import { getSentry } from '@services/sentry';
import { styled } from '@mui/material';
import { getSearchTitleBox } from './helpers/getSearchTitle';
import { AllPrescriptionTypeCheckModal } from './allPrescriptionTypeCheckModal';
import { Loader } from '@components/Loader';
import { BackgroundGlobalStyle } from '@components/PageWrapper';
import { Header } from '@components/Header/Header';
import { ProductInteractionLocation } from '@validators/Events/Product';
import { useEventTracking } from '@hooks/useEventTracking';
import { ScreenEventType } from '@validators/Events/Screen';
import { InteractionEventTypes } from '@validators/Events/Interactions';

const Container = styled('div')(({ theme }) => ({
  width: '100%',
  padding: '10px 10px 0 10px',
  [theme.breakpoints.down(320)]: {
    padding: '10px 0 0 0',
  },
}));
const ProductListContainer = styled('div')({
  width: '100%',
  height: 'fit-content',
  maxWidth: 1200,
  margin: 'auto',
});
const ProductList = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: 320,
  [theme.breakpoints.up(320)]: {
    maxWidth: 'calc(100vw - 20px)',
  },
}));

function SearchResults() {
  const router = useRouter();
  const { track } = useEventTracking();
  const { setLastSearch } = useMekiState();
  const previousSearchRef = useRef<string>('');

  const { q } = router.query;
  const searchString = q ? decodeURI(q as string) : '';

  void track(ScreenEventType.ScreenResultsView);

  const {
    data: pageData,
    loading,
    fetchMore,
    error,
  } = useQuery<ProductQueryResponse, ProductQueryParams>(
    ProductQuery('SearchResults'),
    {
      variables: {
        params: { query: searchString, pageNumber: 1 },
      },
      fetchPolicy: 'cache-first',
      skip: !searchString,
    },
  );
  const titleCarrusel =
    pageData?.products?.perfectMatchProducts?.length > 1
      ? `Resultados ${searchString}`
      : 'Resultados';

  const titleProductBox = getSearchTitleBox(searchString, pageData, loading);
  const productsNodes = pageData?.products?.nodes.filter(
    (product) => product.availability.status !== 'HIDDEN',
  );

  const resultCounts = useMemo(() => {
    const perfectMatchCount =
      pageData?.products?.perfectMatchProducts?.length ?? 0;
    const notPerfectCount = productsNodes?.length ?? 0;
    const totalResults = perfectMatchCount + notPerfectCount;
    return { perfectMatchCount, notPerfectCount, totalResults };
  }, [pageData?.products?.perfectMatchProducts?.length, productsNodes?.length]);

  const allSoldOutResults = useMemo(() => {
    return productsNodes?.every((product) =>
      ['UNAVAILABLE', 'HIDDEN', 'SOLD_OUT'].includes(
        product.availability.status,
      ),
    );
  }, [productsNodes]);

  const allPrescriptionTypeCheck = useMemo(() => {
    const products = pageData?.products;
    const allProducts = [
      ...(products?.perfectMatchProducts ?? []),
      ...(productsNodes ?? []),
    ];
    if (allProducts?.length === 0) return false;
    return allProducts?.every(
      (product) => product.prescriptionType === 'CHECK',
    );
  }, [pageData?.products, productsNodes]);

  const handleFetchMore = useMemo(() => {
    return async () => {
      if (
        pageData?.products?.pageInfo.totalPages <=
        (pageData?.products.pageNumber ?? 1)
      ) {
        return;
      }
      await fetchMore({
        variables: {
          params: {
            query: searchString,
            pageNumber: (pageData?.products.pageNumber ?? 1) + 1,
          },
        },
      });
    };
  }, [
    fetchMore,
    pageData?.products?.pageInfo.totalPages,
    pageData?.products.pageNumber,
    searchString,
  ]);

  const { loading: loadingMore } = useBottomReach(handleFetchMore);

  useEffect(
    function trackLoadMore() {
      if (loading || loadingMore) return null;

      const currentPage = pageData?.products?.pageNumber ?? 1;
      const hasMorePages =
        pageData?.products?.pageInfo.totalPages > currentPage;

      if (hasMorePages && currentPage > 1) {
        void track(InteractionEventTypes.SearchLoadMore, {
          search_string: searchString,
          search_results: resultCounts.totalResults,
          search_load_more_iteration: currentPage,
          search_perfect_results: resultCounts.perfectMatchCount,
          search_not_perfect_results: resultCounts.notPerfectCount,
        });
      }
    },
    [
      loading,
      loadingMore,
      pageData?.products?.pageNumber,
      pageData?.products?.pageInfo.totalPages,
      pageData?.products?.perfectMatchProducts?.length,
      productsNodes?.length,
      searchString,
      track,
      resultCounts,
    ],
  );

  useEffect(
    function trackResults() {
      if (loading) return null;

      const isNewSearch = previousSearchRef.current !== searchString;
      const dataMatchesCurrentSearch =
        pageData?.products?.query === searchString;

      if (
        !loading &&
        Boolean(searchString) &&
        Boolean(pageData?.products?.query) &&
        dataMatchesCurrentSearch &&
        isNewSearch
      ) {
        void track(InteractionEventTypes.Search, {
          search_string: searchString,
          search_results: resultCounts.totalResults,
          search_load_more_iteration: 0,
          search_perfect_results: resultCounts.perfectMatchCount,
          search_not_perfect_results: resultCounts.notPerfectCount,
        });
        previousSearchRef.current = searchString;
      }
    },
    [
      track,
      searchString,
      loading,
      pageData,
      pageData?.products?.query,
      pageData?.products?.totalCount,
      productsNodes?.length,
      resultCounts,
    ],
  );

  useEffect(
    function trackLastSearch() {
      setLastSearch(
        new URLSearchParams(
          router.query as unknown as Record<string, string>,
        ).toString(),
      );
    },
    [router.query, setLastSearch],
  );
  useEffect(
    function reportSearchError() {
      if (error) {
        void getSentry().then((Sentry) => {
          Sentry.captureException(error, {
            extra: { searchString },
          });
        });
      }
    },
    [error, searchString],
  );
  return (
    <>
      <BackgroundGlobalStyle />
      <Header hasSearchBox={true} />
      <SearchContentContainer>
        {error ? (
          error.message === 'QUERY_MIN_LENGTH' ? (
            <SearchNotFound />
          ) : (
            <SearchError />
          )
        ) : productsNodes?.length > 0 ||
          pageData?.products?.perfectMatchProducts?.length > 0 ||
          loading ||
          loadingMore ? (
          <>
            {loading && <Loader />}
            {pageData?.products?.perfectMatchProducts?.length > 0 &&
              (allSoldOutResults || productsNodes?.length == 0 ? (
                <Products
                  data={pageData?.products?.perfectMatchProducts.map(
                    (product) => ({
                      product,
                    }),
                  )}
                  loading={loading || loadingMore}
                  title={titleCarrusel}
                  location={ProductInteractionLocation.SearchResults}
                />
              ) : (
                <Container>
                  <ProductListContainer>
                    <ProductList>
                      <Products.Carousel
                        data={pageData?.products?.perfectMatchProducts.map(
                          (product) => ({
                            product,
                          }),
                        )}
                        totalCount={
                          pageData?.products?.perfectMatchProducts?.length
                        }
                        loading={loading}
                        title={titleCarrusel}
                        location={ProductInteractionLocation.SearchResults}
                      />
                    </ProductList>
                  </ProductListContainer>
                </Container>
              ))}
            {productsNodes?.length > 0 &&
              (pageData?.products?.perfectMatchProducts?.length > 0 ? (
                !allSoldOutResults && (
                  <Products
                    data={
                      productsNodes?.map((product) => ({
                        product,
                      })) ?? []
                    }
                    loading={loading || loadingMore}
                    title={titleProductBox}
                    location={ProductInteractionLocation.SearchResults}
                  />
                )
              ) : (
                <Products
                  data={
                    productsNodes?.map((product) => ({
                      product,
                    })) ?? []
                  }
                  loading={loading || loadingMore}
                  title={titleProductBox}
                  location={ProductInteractionLocation.SearchResults}
                />
              ))}
          </>
        ) : (
          <SearchNotFound />
        )}
      </SearchContentContainer>
      {allPrescriptionTypeCheck && pageData?.products && (
        <AllPrescriptionTypeCheckModal />
      )}
    </>
  );
}

const MemoizedSearchResults = memo(SearchResults);
export { MemoizedSearchResults as SearchResults };
